'use client'

import React, { useMemo, useState } from 'react'

import { twMerge } from 'tailwind-merge'

import type { ContractSizeHistogram } from '@/types/usaspending'

import { Currency, NumberDisplay } from '../Formatters'

export interface HistogramChartProps {
  data: ContractSizeHistogram[]
  height?: number
  accentColor?: 'blue' | 'indigo' | 'purple' | 'teal' | 'amber'
  className?: string
}

const ACCENT_COLORS = {
  blue: {
    bar: 'bg-blue-500 dark:bg-blue-400',
    barHover: 'bg-blue-600 dark:bg-blue-300',
    text: 'text-blue-600 dark:text-blue-400',
  },
  indigo: {
    bar: 'bg-indigo-500 dark:bg-indigo-400',
    barHover: 'bg-indigo-600 dark:bg-indigo-300',
    text: 'text-indigo-600 dark:text-indigo-400',
  },
  purple: {
    bar: 'bg-purple-500 dark:bg-purple-400',
    barHover: 'bg-purple-600 dark:bg-purple-300',
    text: 'text-purple-600 dark:text-purple-400',
  },
  teal: {
    bar: 'bg-teal-500 dark:bg-teal-400',
    barHover: 'bg-teal-600 dark:bg-teal-300',
    text: 'text-teal-600 dark:text-teal-400',
  },
  amber: {
    bar: 'bg-amber-500 dark:bg-amber-400',
    barHover: 'bg-amber-600 dark:bg-amber-300',
    text: 'text-amber-600 dark:text-amber-400',
  },
}

/**
 * HistogramChart
 *
 * Vertical bar chart for contract size distribution.
 * Shows award counts bucketed into size ranges.
 */
export const HistogramChart: React.FC<HistogramChartProps> = ({
  data,
  height = 140,
  accentColor = 'purple',
  className,
}) => {
  const [hoveredIndex, setHoveredIndex] = useState<number | null>(null)
  const colors = ACCENT_COLORS[accentColor]

  // Find max count for scaling bar heights
  const maxCount = useMemo(
    () => Math.max(...data.map((d) => d.count), 1),
    [data],
  )

  // Calculate summary statistics
  const stats = useMemo(() => {
    if (data.length === 0) return { total: 0, median: '$0-$25K', count: 0 }

    const totalCount = data.reduce((sum, d) => sum + d.count, 0)
    const totalAmount = data.reduce((sum, d) => sum + d.totalAmount, 0)

    // Find median bucket
    let cumulative = 0
    let medianBucket = data[0]?.bucket || '$0-$25K'
    for (const bucket of data) {
      cumulative += bucket.count
      if (cumulative >= totalCount / 2) {
        medianBucket = bucket.bucket
        break
      }
    }

    return {
      total: totalAmount,
      count: totalCount,
      medianBucket,
    }
  }, [data])

  if (data.length === 0) {
    return (
      <div
        className={twMerge(
          'flex items-center justify-center rounded-lg border border-dashed border-primary-300 dark:border-primary-700',
          className,
        )}
        style={{ height }}
      >
        <span className="text-sm text-primary-400 dark:text-primary-500">
          No data available
        </span>
      </div>
    )
  }

  const chartHeight = height - 24 // Leave room for labels

  return (
    <div className={className}>
      {/* Chart area with bars */}
      <div className="relative" style={{ height: chartHeight }}>
        {/* Grid lines */}
        <div className="absolute inset-0 flex flex-col justify-between pointer-events-none">
          {[0, 1, 2, 3].map((i) => (
            <div
              key={i}
              className="border-b border-dashed border-primary-200 dark:border-primary-700 opacity-50"
            />
          ))}
        </div>

        {/* Bars container */}
        <div className="absolute inset-0 flex items-end gap-2 px-2">
          {data.map((bucket, index) => {
            // Calculate bar height as percentage, minimum 4px if count > 0
            const heightPercent =
              maxCount > 0 ? (bucket.count / maxCount) * 100 : 0
            const isHovered = hoveredIndex === index

            return (
              <div
                key={bucket.bucket}
                className="relative flex-1 flex flex-col items-center justify-end h-full"
                onMouseEnter={() => setHoveredIndex(index)}
                onMouseLeave={() => setHoveredIndex(null)}
              >
                {/* Count label above bar */}
                {bucket.count > 0 && (
                  <div
                    className={twMerge(
                      'absolute text-[10px] font-semibold tabular-nums text-primary-600 dark:text-primary-300 transition-all duration-150',
                      isHovered ? 'opacity-100' : 'opacity-70',
                    )}
                    style={{
                      bottom: `calc(${Math.max(heightPercent, 3)}% + 4px)`,
                    }}
                  >
                    {bucket.count}
                  </div>
                )}

                {/* Bar */}
                <div
                  className={twMerge(
                    'w-full rounded-t-sm cursor-pointer transition-all duration-200',
                    isHovered ? colors.barHover : colors.bar,
                  )}
                  style={{
                    height: `${heightPercent}%`,
                    minHeight: bucket.count > 0 ? 6 : 0,
                  }}
                />

                {/* Tooltip on hover */}
                {isHovered && bucket.count > 0 && (
                  <div className="absolute bottom-full left-1/2 -translate-x-1/2 mb-8 z-20 animate-[fadeIn_100ms_ease-out]">
                    <div className="whitespace-nowrap rounded-lg border border-primary-200 bg-white px-3 py-2 text-center shadow-lg dark:border-primary-700 dark:bg-primary-900">
                      <div className="text-xs font-semibold text-primary-900 dark:text-primary-100">
                        {bucket.bucket}
                      </div>
                      <div
                        className={twMerge(
                          'text-sm font-bold tabular-nums',
                          colors.text,
                        )}
                      >
                        {bucket.count} award{bucket.count !== 1 ? 's' : ''}
                      </div>
                      <div className="text-[10px] text-primary-500 dark:text-primary-400">
                        <Currency value={bucket.totalAmount} compact /> total
                      </div>
                    </div>
                    <div className="absolute -bottom-1 left-1/2 h-2 w-2 -translate-x-1/2 rotate-45 border-b border-r border-primary-200 bg-white dark:border-primary-700 dark:bg-primary-900" />
                  </div>
                )}
              </div>
            )
          })}
        </div>
      </div>

      {/* X-axis bucket labels */}
      <div className="flex gap-2 px-2 mt-1">
        {data.map((bucket) => (
          <div
            key={bucket.bucket}
            className="flex-1 text-center text-[9px] font-medium text-primary-500 dark:text-primary-400 truncate"
          >
            {bucket.bucket}
          </div>
        ))}
      </div>

      {/* Summary stats - stack on mobile, row on sm+ */}
      <div className="mt-3 grid grid-cols-1 gap-2 rounded-lg bg-primary-50 px-3 py-2 sm:grid-cols-3 sm:gap-0 dark:bg-primary-800/50">
        <div className="flex items-center justify-between sm:block">
          <span className="text-xs text-primary-500 dark:text-primary-400">
            Total Awards
          </span>
          <div className="text-sm font-semibold tabular-nums text-primary-900 dark:text-primary-100">
            <NumberDisplay value={stats.count} />
          </div>
        </div>
        <div className="flex items-center justify-between border-t border-primary-200 pt-2 sm:block sm:border-t-0 sm:pt-0 sm:text-center dark:border-primary-700">
          <span className="text-xs text-primary-500 dark:text-primary-400">
            Median Range
          </span>
          <div className="text-sm font-semibold tabular-nums text-primary-900 dark:text-primary-100">
            {stats.medianBucket}
          </div>
        </div>
        <div className="flex items-center justify-between border-t border-primary-200 pt-2 sm:block sm:border-t-0 sm:pt-0 sm:text-right dark:border-primary-700">
          <span className="text-xs text-primary-500 dark:text-primary-400">
            Total Value
          </span>
          <div className="text-sm font-semibold tabular-nums text-primary-900 dark:text-primary-100">
            <Currency value={stats.total} compact />
          </div>
        </div>
      </div>

      {/* Animation styles */}
      <style jsx>{`
        @keyframes fadeIn {
          from {
            opacity: 0;
            transform: translateX(-50%) translateY(4px);
          }
          to {
            opacity: 1;
            transform: translateX(-50%) translateY(0);
          }
        }
      `}</style>
    </div>
  )
}
